import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FeatherIcon from 'feather-icons-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

import { API_URL, ENDPOINTS } from '../constants/api';
import { getToken, decodeToken } from '../utils/TokenUtils';
import DetailData from './DetailData';
import { useTranslation } from 'react-i18next';
import CustomerCareDashboard from './CustomerCareDashboard';
import CustomerPortalDashbpard from './CustomerPortalDashbpard';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function Charts() {
  const token = getToken();
  const { t } = useTranslation();
  const [stats, setStats] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(true);

  const userType = decodeToken(token, 'userLevel');

  const [selectedId, setSelectedId] = useState(null);
  const [chartTitle, setChartTitle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allowedUserTypes = [
    'eng_development',
    'customer_care',
    'eng_customer_care',
    'admin',
    'developer',
  ];

  const isSamaCompany = API_URL.includes('samacompany') || API_URL.includes('gure');
  const isAllowedAPI = API_URL === 'https://newktcapi.kaafi.net';
  const isAllowedUser = allowedUserTypes.includes(userType);

  const openModal = (id, title) => {
    setSelectedId(id);
    setChartTitle(title);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedId(0);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const response = await axios.get(`${API_URL}/${ENDPOINTS.SYS}/get-charts`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        setStats(response.data || []);
      } catch (error) {
        console.error('Error fetching stats:', error.message);
        setStats([]);
      }
    };
    fetchCharts();
  }, [token]);

  const getMonthTitle = () => {
    const now = new Date();
    return now.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    if (!isSamaCompany) {
      setLoadingChart(false);
      return;
    }
    const fetchSalesByProduct = async () => {
      setLoadingChart(true);
      try {
        const response = await axios.get(`${API_URL}/${ENDPOINTS.SYS}/getlink/sales-by-product`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const report = response.data?.report || [];
        
        // Gradient color effect for chart bars
        setSalesData({
          labels: report.map((item) => item.product),
          datasets: [
            {
              label: 'Total Sales',
              data: report.map((item) => item.total),
              backgroundColor: 'rgba(240, 128, 28, 0.85)',
              hoverBackgroundColor: '#F0801C',
              borderRadius: 8,
              borderSkipped: false,
              barThickness: 28,
            },
          ],
        });
      } catch (err) {
        console.error('Error fetching sales by product:', err.message);
        setSalesData(null);
      } finally {
        setLoadingChart(false);
      }
    };
    fetchSalesByProduct();
  }, [isSamaCompany, token]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => ' Sales: KSh ' + ctx.parsed.y.toLocaleString(),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 12, weight: '500' } },
      },
      y: {
        grid: { color: 'rgba(226, 232, 240, 0.6)', drawBorder: false },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
          callback: (v) => 'KSh ' + v.toLocaleString(),
        },
      },
    },
  };

  // Map background color strings to elegant Tailwind-like gradient themes
  const getCardTheme = (bgColor) => {
    switch (bgColor) {
      case 'primary':
        return { bg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', text: '#ffffff' };
      case 'success':
        return { bg: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', text: '#ffffff' };
      case 'warning':
        return { bg: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)', text: '#ffffff' };
      case 'danger':
        return { bg: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', text: '#ffffff' };
      case 'info':
        return { bg: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)', text: '#ffffff' };
      default:
        return { bg: 'linear-gradient(135deg, #64748b 0%, #334155 100%)', text: '#ffffff' };
    }
  };

  return (
    <>
      <style>{`
        .metric-card {
          border: none;
          border-radius: 16px;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 15px -2px rgba(0, 0, 0, 0.05);
        }
        .metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 25px -5px rgba(0, 0, 0, 0.12);
        }
        .icon-badge {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .support-banner {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border: 1px solid #bfdbfe;
          border-radius: 16px;
        }
      `}</style>

      <div className="container-fluid px-4 py-3">
        {/* Support Alert Banner */}
        <div className="support-banner p-3 p-md-4 mb-4 shadow-sm d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="p-3 bg-primary text-white rounded-circle d-none d-sm-flex align-items-center justify-content-center">
              <FeatherIcon icon="help-circle" size="22" />
            </div>
            <div>
              <h6 className="fw-bold text-dark mb-1">
                Fadlan wixii cabasho, Cillad iyo Tala bixin ah nagula wadaag
              </h6>
              <p className="text-muted small mb-0">
                Submit queries, feedback, or system technical issues directly to our support team.
              </p>
            </div>
          </div>
          <button
            className="btn btn-primary rounded-pill px-4 py-2 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2"
            onClick={() => window.open('https://portal.kaafi.net/login/portal-login')}
          >
            <span>Halkaan Riix</span>
            <FeatherIcon icon="arrow-right" size="16" />
          </button>
        </div>

        {/* Dashboard Title & Actions */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="fw-bold text-slate-800 mb-1">{t('Dashboard')}</h3>
            <span className="text-muted small">System overview and real-time metrics</span>
          </div>
        </div>

        {/* Detail Modal Popover */}
        {selectedId && (
          <DetailData
            token={token}
            id={selectedId}
            route="get-chart-detail"
            title={chartTitle}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
          />
        )}

        {/* Stat Cards Grid */}
        <div className="row g-3">
          {stats && stats.length > 0 ? (
            stats.map((stat, index) => {
              const theme = getCardTheme(stat.bg_color);
              return (
                <div
                  className="col-12 col-sm-6 col-md-3"
                  key={index}
                  onClick={() => openModal(stat.id, stat.title)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="metric-card p-3 h-100 d-flex flex-column justify-content-between"
                    style={{ background: theme.bg, color: theme.text }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-medium small opacity-90 text-uppercase tracking-wider">
                        {t(stat.title)}
                      </span>
                      <div className="icon-badge">
                        <FeatherIcon icon={stat.icon || 'activity'} size="20" className="text-white" />
                      </div>
                    </div>
                    <div>
                      <h2 className="fw-bold mb-0 text-white tracking-tight">{stat.value}</h2>
                      <div className="d-flex align-items-center gap-1 mt-2 opacity-75 small">
                        <span>View details</span>
                        <FeatherIcon icon="chevron-right" size="14" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12">
              <div className="p-4 text-center bg-light rounded-4 text-muted small">
                No stat cards available.
              </div>
            </div>
          )}
        </div>

        {/* Sales by Product Bar Chart */}
        {isSamaCompany && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 rounded-4 shadow-sm p-3 p-md-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h5 className="fw-bold text-dark mb-1">Sales by Product</h5>
                    <span className="text-muted small">{getMonthTitle()}</span>
                  </div>
                  <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 small fw-semibold">
                    Monthly Performance
                  </span>
                </div>

                <div className="card-body p-0">
                  {loadingChart ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : salesData ? (
                    <div style={{ position: 'relative', height: '340px' }}>
                      <Bar data={salesData} options={barOptions} />
                    </div>
                  ) : (
                    <div className="text-center text-muted py-5">
                      <FeatherIcon icon="bar-chart-2" size="32" className="mb-2 text-secondary opacity-50" />
                      <div>No sales data available for this period</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Role-based Embedded Dashboards */}
      {isAllowedAPI && isAllowedUser && <CustomerCareDashboard />}
      {isAllowedAPI && userType === 'customer' && <CustomerPortalDashbpard />}
    </>
  );
}